import { IApiDatafanPipeline } from "@smp/api";
import { parse } from "date-fns";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import subMinutes from "date-fns/subMinutes";

import {
  IDatafanPipelineType,
  useQueryDatafanPipelineTypes,
} from "@/queries/datafan";

import { useDatafanPipelineDataStatus } from "./use_datafan_pipeline_data_status";
import { useDatafanPipelineStatus } from "./use_datafan_pipeline_status";

export interface IDatafanPipeline extends IApiDatafanPipeline {
  aggregated_status: ReturnType<ReturnType<typeof useDatafanPipelineStatus>>;
  enable_fast_run: boolean;
  data_first_date: string | null;
  data_last_date: string | null;
  first_date_request: string | null;
  data_status: ReturnType<ReturnType<typeof useDatafanPipelineDataStatus>>;
  profile_picture: string | undefined;
  type: IDatafanPipelineType | undefined;
  update_at_formatted: string;
}

export const useDatafanPipelineAggregation = () => {
  const aggregatePipelineDataStatus = useDatafanPipelineDataStatus();
  const aggregatePipelineStatus = useDatafanPipelineStatus();
  const { data: pipelineTypes } = useQueryDatafanPipelineTypes();

  return (pipeline: IApiDatafanPipeline) => {
    const { firstDate, lastDate, firstDateRequest } =
      pipeline.report_status.reduce(
        (acc, { first_date, last_date, first_date_request }) => {
          if (first_date_request) {
            const firstDateRequest = new Date(first_date_request);

            if (
              !acc.firstDateRequest ||
              acc.firstDateRequest > firstDateRequest
            ) {
              acc.firstDateRequest = firstDateRequest;
            }
          }

          if (first_date) {
            const firstDate = new Date(first_date);

            if (!acc.firstDate || acc.firstDate > firstDate) {
              acc.firstDate = firstDate;
            }
          }

          if (last_date) {
            const lastDate = new Date(last_date);

            if (!acc.lastDate || acc.lastDate < lastDate) {
              acc.lastDate = lastDate;
            }
          }

          return acc;
        },
        {
          firstDate: null,
          firstDateRequest: null,
          lastDate: null,
        } as {
          firstDate: Date | null;
          firstDateRequest: Date | null;
          lastDate: Date | null;
        },
      );

    const profile_picture = pipeline.streams.find(
      ({ profile_picture }) => !!profile_picture,
    )?.profile_picture;

    const pipelineType = pipelineTypes?.[pipeline.source];

    const now = new Date();

    const updateAtFormatted = format(
      pipeline.data_update_at
        ? parseISO(pipeline.data_update_at)
        : subMinutes(
            parse(pipeline.update_at, "yyyy-MM-dd HH:mm", now),
            now.getTimezoneOffset(),
          ),
      "d MMMM, HH:mm",
    );

    return {
      ...pipeline,
      aggregated_status: aggregatePipelineStatus(pipeline),
      data_first_date: firstDate?.toISOString() || null,
      data_last_date: lastDate?.toISOString() || null,
      data_status: aggregatePipelineDataStatus(pipeline),
      enable_fast_run: Boolean(pipelineType?.enable_fast_run),
      first_date_request: firstDateRequest?.toISOString() || null,
      profile_picture,
      type: pipelineType,
      update_at_formatted: updateAtFormatted,
    };
  };
};
